import React, { useEffect, useState, useCallback } from 'react';
import { Ic, ICONS } from '../../data/icons';

// ── AVATAR ────────────────────────────────────────────────────────────────────
export const Avatar = ({ initials, size = 32, gradient }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    background: gradient || "linear-gradient(135deg,#3b82f6,#8b5cf6)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.33, fontWeight: 700, color: "#fff",
  }}>{initials}</div>
);

// ── BADGE ─────────────────────────────────────────────────────────────────────
export const Badge = ({ color, children, small }) => (
  <span style={{
    fontSize: small ? 10 : 11, fontWeight: 600,
    padding: small ? "2px 6px" : "3px 9px", borderRadius: 6,
    background: `${color}20`, color, border: `1px solid ${color}33`,
    whiteSpace: "nowrap",
  }}>{children}</span>
);

// ── STATUS DOT ────────────────────────────────────────────────────────────────
export const StatusDot = ({ online }) => (
  <span style={{
    width: 7, height: 7, borderRadius: "50%", display: "inline-block", flexShrink: 0,
    background: online ? "#22c55e" : "#475569",
    boxShadow: online ? "0 0 8px #22c55e99" : "none",
  }} />
);

// ── MINI BAR ──────────────────────────────────────────────────────────────────
export const MiniBar = ({ pct, color, C }) => (
  <div style={{ height: 4, borderRadius: 2, background: C?.border2 || "rgba(255,255,255,0.12)", overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 2, transition: "width 1s ease" }} />
  </div>
);

// ── CHART TOOLTIP ─────────────────────────────────────────────────────────────
export const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1e293b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "10px 14px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
      <div style={{ color: "#94a3b8", marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
          {p.name}: <strong style={{ color: "#fff" }}>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── CARD ──────────────────────────────────────────────────────────────────────
export const Card = ({ children, C, style = {} }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: C.shadowSm, ...style }}>
    {children}
  </div>
);

export const CardHeader = ({ title, action, C }) => (
  <div style={{ padding: "13px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <span style={{ fontWeight: 700, color: C.text, fontSize: 13.5 }}>{title}</span>
    {action}
  </div>
);

// ── MODAL ─────────────────────────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, onConfirm, title, message, type = "info", C, children, confirmLabel, cancelLabel, wide, extraWide }) => {
  const defaultColors = {
    surface: "#1e293b",
    border: "rgba(100,100,120,0.2)",
    border2: "rgba(100,100,120,0.15)",
    text: "inherit",
    textSub: "#94a3b8"
  };
  const theme = C || defaultColors;
  
  useEffect(() => {
    if (!isOpen) return;
    const handle = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const colors = {
    danger: { accent: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.3)" },
    warning:{ accent: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)" },
    info:   { accent: "#3b82f6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.3)" },
    success:{ accent: "#22c55e", bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.3)" },
  };
  const col = colors[type] || colors.info;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }} />
      <div style={{
        position: "relative", background: theme.surface, border: `1px solid ${theme.border2}`,
        borderRadius: 16, padding: "24px 24px 20px",
        width: extraWide ? "min(960px,96vw)" : wide ? "min(700px,95vw)" : "min(440px,95vw)",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
        animation: "modalIn 0.18s ease", boxSizing: "border-box",
      }}>
        <style>{`@keyframes modalIn{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}`}</style>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: "none", border: `1px solid ${theme.border}`, borderRadius: 7, cursor: "pointer", color: theme.textSub, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", zIndex:1 }}>
          <Ic d={ICONS.x} size={13} />
        </button>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: children ? 16 : 14, flexShrink: 0 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: col.bg, border: `1px solid ${col.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic d={type === "danger" ? ICONS.alert : type === "warning" ? ICONS.alert : ICONS.info} size={20} s={col.accent} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 6, letterSpacing: "-0.3px" }}>{title}</div>
            {message && <div style={{ fontSize: 13, color: theme.textSub, lineHeight: 1.55 }}>{message}</div>}
          </div>
        </div>
        {children && <div style={{ marginBottom: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>{children}</div>}
        {(onConfirm || onClose) && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, flexShrink: 0 }}>
            <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 9, border: `1px solid ${theme.border}`, background: "transparent", color: theme.textSub, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {cancelLabel || "İptal"}
            </button>
            {onConfirm && (
              <button onClick={() => { onConfirm(); onClose(); }} style={{ padding: "8px 18px", borderRadius: 9, border: "none", background: col.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {confirmLabel || (type === "danger" ? "Onayla" : "Tamam")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── TOAST SYSTEM ──────────────────────────────────────────────────────────────
let _addToast = null;
export const setToastRef = fn => { _addToast = fn; };
export const toast = {
  success: (msg) => _addToast?.({ type: "success", msg }),
  error:   (msg) => _addToast?.({ type: "error",   msg }),
  info:    (msg) => _addToast?.({ type: "info",    msg }),
  warning: (msg) => _addToast?.({ type: "warning", msg }),
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((t) => {
    const id = Date.now();
    setToasts(prev => [...prev, { ...t, id }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
  }, []);

  useEffect(() => { setToastRef(add); return () => setToastRef(null); }, [add]);

  const colors = { success: "#22c55e", error: "#ef4444", info: "#3b82f6", warning: "#f59e0b" };
  const icons  = { success: ICONS.check, error: ICONS.x, info: ICONS.info, warning: ICONS.alert };

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none" }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "#1e293b", border: `1px solid ${colors[t.type]}44`,
          borderRadius: 12, padding: "12px 18px", minWidth: 260, maxWidth: 380,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          animation: "toastIn 0.2s ease",
          pointerEvents: "auto",
        }}>
          <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${colors[t.type]}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic d={icons[t.type]} size={14} s={colors[t.type]} />
          </div>
          <span style={{ fontSize: 13, color: "#f1f5f9", fontWeight: 500 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
};

// ── SKELETON ──────────────────────────────────────────────────────────────────
export const Skeleton = ({ width = "100%", height = 16, radius = 6, C }) => (
  <div style={{
    width, height, borderRadius: radius,
    background: C?.border2 || "rgba(255,255,255,0.08)",
    overflow: "hidden", position: "relative",
  }}>
    <div style={{
      position: "absolute", inset: 0,
      background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
      animation: "shimmer 1.4s infinite",
    }} />
    <style>{`@keyframes shimmer{from{transform:translateX(-100%)}to{transform:translateX(100%)}}`}</style>
  </div>
);

export const TableSkeleton = ({ rows = 5, cols = 5, C }) => (
  <div>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ display: "flex", gap: 16, padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
        {Array.from({ length: cols }).map((_, j) => (
          <Skeleton key={j} width={j === 0 ? "30%" : `${Math.floor(60 / (cols - 1))}%`} height={12} C={C} />
        ))}
      </div>
    ))}
  </div>
);

// ── PAGINATION ────────────────────────────────────────────────────────────────
export const Pagination = ({ page, total, perPage = 10, onChange, C }) => {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) pages.push(i);

  const btn = (content, p, disabled = false, active = false) => (
    <button key={p} onClick={() => !disabled && onChange(p)} disabled={disabled} style={{
      width: 32, height: 32, borderRadius: 8, border: `1px solid ${active ? C.accentBorder : C.border}`,
      background: active ? C.accentBg : "transparent", color: active ? C.accent : disabled ? C.textFaint : C.textSub,
      fontSize: 12.5, fontWeight: active ? 700 : 400, cursor: disabled ? "default" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
    }}>{content}</button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, color: C.textFaint }}>Toplam {total} kayıt · Sayfa {page}/{totalPages}</span>
      <div style={{ display: "flex", gap: 4 }}>
        {btn(<Ic d={ICONS.chevL} size={12} />, page - 1, page === 1)}
        {page > delta + 1 && <>{btn(1, 1)}{page > delta + 2 && <span style={{ color: C.textFaint, alignSelf: "center", fontSize: 12 }}>…</span>}</>}
        {pages.map(p => btn(p, p, false, p === page))}
        {page < totalPages - delta && <>{page < totalPages - delta - 1 && <span style={{ color: C.textFaint, alignSelf: "center", fontSize: 12 }}>…</span>}{btn(totalPages, totalPages)}</>}
        {btn(<Ic d={ICONS.chevR} size={12} />, page + 1, page === totalPages)}
      </div>
    </div>
  );
};

// ── FORM FIELD ────────────────────────────────────────────────────────────────
export const FormField = ({ label, required, error, C, children }) => (
  <div>
    <label style={{ fontSize: 11.5, fontWeight: 600, color: error ? "#ef4444" : C.textFaint, display: "block", marginBottom: 6, letterSpacing: "0.3px" }}>
      {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{error}</div>}
  </div>
);

export const Input = ({ C, error, style = {}, ...props }) => (
  <input {...props} style={{
    width: "100%", borderRadius: 9, padding: "9px 12px", fontSize: 13,
    border: `1px solid ${error ? "#ef4444" : C.border}`,
    background: C.surface2, color: C.text, fontFamily: "inherit", outline: "none",
    transition: "border-color 0.15s",
    ...style,
  }}
    onFocus={e => e.target.style.borderColor = error ? "#ef4444" : C.accent}
    onBlur={e => e.target.style.borderColor = error ? "#ef4444" : C.border}
  />
);

export const Select = ({ C, children, style = {}, ...props }) => (
  <select {...props} style={{
    width: "100%", height: 38, borderRadius: 9, padding: "0 10px", fontSize: 13,
    border: `1px solid ${C.border}`, background: C.surface2, color: C.text,
    fontFamily: "inherit", outline: "none", cursor: "pointer", ...style,
  }}>{children}</select>
);

// ── SEARCH INPUT ──────────────────────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = "Ara...", C, style = {} }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "0 14px", height: 38, ...style }}>
    <Ic d={ICONS.search} size={15} s={C.textFaint} />
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ background: "none", border: "none", outline: "none", color: C.text, fontSize: 13, width: "100%", fontFamily: "inherit" }} />
    {value && <button onClick={() => onChange("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.textFaint, display: "flex", padding: 0 }}><Ic d={ICONS.x} size={13} /></button>}
  </div>
);

// ── PLACEHOLDER PAGE ──────────────────────────────────────────────────────────
export const PlaceholderPage = ({ C, title }) => (
  <div style={{ textAlign: "center", padding: "90px 24px" }}>
    <div style={{ width: 70, height: 70, borderRadius: 20, background: C.accentBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
      <Ic d={ICONS.lock} size={30} s={C.accent} />
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 10, letterSpacing: "-0.5px" }}>{title}</div>
    <div style={{ color: C.textSub, fontSize: 14, marginBottom: 28 }}>{title} modülü yakında eklenecek.</div>
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 22px", borderRadius: 10, background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 13, fontWeight: 700 }}>
      <Ic d={ICONS.zap} size={14} s={C.accent} /> Yakında gelecek
    </div>
  </div>
);

// ── EMPTY STATE ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon = "search", title = "Sonuç bulunamadı", desc, C, action, actionLabel }) => (
  <div style={{ textAlign: "center", padding: "52px 24px" }}>
    <div style={{ width: 52, height: 52, borderRadius: 14, background: C?.surface2 || "rgba(100,100,120,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <Ic d={ICONS[icon]} size={22} s={C?.textFaint || "#94a3b8"} />
    </div>
    <div style={{ fontSize: 14, fontWeight: 700, color: C?.text || "inherit", marginBottom: 6 }}>{title}</div>
    {desc && <div style={{ fontSize: 12, color: C?.textFaint || "#94a3b8", marginBottom: 16 }}>{desc}</div>}
    {action && actionLabel && (
      <button 
        onClick={action}
        style={{
          padding: "8px 16px",
          borderRadius: 8,
          background: C?.accent || "#3b82f6",
          color: "#fff",
          border: "none",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s"
        }}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// ── STAT CARD ─────────────────────────────────────────────────────────────────
export const StatCard = ({ label, value, change, icon, color, bg, sub, C }) => {
  const isNeg = label?.includes("İhlal") || label?.includes("Hata");
  const isUp  = change > 0;
  const cc    = isNeg ? (isUp ? C.red : C.green) : (isUp ? C.green : C.red);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14, boxShadow: C.shadowSm, cursor: "default", position: "relative", overflow: "hidden", transition: "transform 0.18s,box-shadow 0.18s" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = C.shadow; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = C.shadowSm; }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Ic d={ICONS[icon]} size={20} s={color} />
        </div>
        {change !== undefined && (
          <div style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99, background: isNeg === isUp ? C.redBg : C.greenBg, color: cc, display: "flex", alignItems: "center", gap: 3 }}>
            <Ic d={isUp ? ICONS.up : ICONS.down} size={10} s={cc} /> {Math.abs(change)}
          </div>
        )}
      </div>
      <div>
        <div style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: "-1.5px", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12.5, color: C.textSub, marginTop: 5, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>{sub}</div>}
      </div>
      <div style={{ position: "absolute", right: -20, bottom: -20, width: 80, height: 80, borderRadius: "50%", background: bg, opacity: 0.6, pointerEvents: "none" }} />
    </div>
  );
};

// ── BUTTON ────────────────────────────────────────────────────────────────────
export const Button = ({ children, onClick, disabled, variant = "primary", C, style = {}, ...props }) => {
  const variants = {
    primary: { bg: C?.accent || "#3b82f6", color: "#fff", border: "none" },
    secondary: { bg: "transparent", color: C?.textSub || "#94a3b8", border: `1px solid ${C?.border || "#e2e8f0"}` },
    danger: { bg: "#ef4444", color: "#fff", border: "none" },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 16px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s",
        ...v,
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
};
